/**
 * Dotypos POS System Integration Service
 *
 * Handles communication with Dotypos API for employee import
 * API Documentation: https://docs.api.dotypos.com/
 */

import { supabase } from '@/integrations/supabase/client';
import {
  DotyposEmployee,
  DotyposConfig,
  DotyposAuthToken,
  MappedEmployee,
  ImportConflict,
  ImportEmployeeResult,
  ImportSummary,
  EmailIssue,
  EmailResolution,
  SmartMatch,
  SmartMatchResolution,
  PushEmployeeResult,
  PushEmployeesSummary,
  ExportMode,
  EmployeeSyncInfo,
  DotyposSyncStatus,
} from '@/types/dotypos';
import { Employee } from '@/types';

const DOTYPOS_API_BASE = 'https://api.dotykacka.cz/v2';
const DOTYPOS_OAUTH_URL = 'https://api.dotykacka.cz/oauth/v2/token';

/**
 * DotyposService - Handles all Dotypos API interactions
 */
export class DotyposService {
  /**
   * Authenticate with Dotypos OAuth2 and get access token
   */
  static async authenticate(
    clientId: string,
    clientSecret: string
  ): Promise<DotyposAuthToken> {
    const params = new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: clientId,
      client_secret: clientSecret,
    });

    const response = await fetch(DOTYPOS_OAUTH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Dotypos authentication failed: ${response.status} - ${errorText}`
      );
    }

    const data: DotyposAuthToken = await response.json();
    return data;
  }

  /**
   * Test connection to Dotypos API with provided credentials
   */
  static async testConnection(
    clientId: string,
    clientSecret: string,
    cloudId: string
  ): Promise<boolean> {
    try {
      const token = await this.authenticate(clientId, clientSecret);

      // Try to fetch a minimal amount of data to verify access
      const response = await fetch(
        `${DOTYPOS_API_BASE}/clouds/${cloudId}/employees?limit=1`,
        {
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Dotypos connection test failed:', error);
      return false;
    }
  }

  /**
   * Fetch all employees from Dotypos
   */
  static async fetchEmployees(
    config: Pick<DotyposConfig, 'client_id' | 'client_secret' | 'cloud_id'> & { business_id?: string; refresh_token?: string }
  ): Promise<DotyposEmployee[]> {
    // If refresh_token is available, use the pass-generator-server endpoint
    if (config.refresh_token && config.business_id) {
      const apiBase = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';

      const response = await fetch(`${apiBase}/dotypos/fetch-employees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: config.business_id,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to fetch employees from Dotypos');
      }

      return data.employees as DotyposEmployee[];
    }

    // Fallback: Use client credentials directly
    const token = await this.authenticate(config.client_id, config.client_secret);

    // Fetch employees
    const response = await fetch(
      `${DOTYPOS_API_BASE}/clouds/${config.cloud_id}/employees`,
      {
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch employees: ${response.status} - ${errorText}`
      );
    }

    const employees: DotyposEmployee[] = await response.json();
    return employees;
  }

  /**
   * Map Dotypos employee to TagInn employee format
   */
  static mapEmployee(
    dotyposEmployee: DotyposEmployee,
    defaultRoleId?: string | null,
    emailOverride?: string
  ): MappedEmployee {
    // Use email override if provided, otherwise use Dotypos email or generate placeholder
    const email = emailOverride || dotyposEmployee.email || this.generatePlaceholderEmail(dotyposEmployee.id);

    return {
      dotyposId: dotyposEmployee.id,
      name: dotyposEmployee.name,
      email,
      phone: dotyposEmployee.phone || null,
      hexColor: dotyposEmployee.hexColor || null,
      enabled: dotyposEmployee.enabled,
      accessLevel: dotyposEmployee.accessLevel || null,
      tags: dotyposEmployee.tags || null,
      roleId: defaultRoleId || null,
      contractStatus: dotyposEmployee.enabled ? 'active' : 'inactive',
    };
  }

  /**
   * Detect conflicts between Dotypos employees and existing TagInn employees
   */
  static async detectConflicts(
    businessId: string,
    dotyposEmployees: DotyposEmployee[]
  ): Promise<ImportConflict[]> {
    // Fetch existing employees including dotypos_employee_id
    const { data: existingEmployees, error } = await supabase
      .from('employees')
      .select('id, name, email, role_id, contract_status, dotypos_employee_id')
      .eq('business_id', businessId);

    if (error) {
      throw new Error(`Failed to fetch existing employees: ${error.message}`);
    }

    const conflicts: ImportConflict[] = [];

    // Check each Dotypos employee for conflicts
    for (const dotyposEmp of dotyposEmployees) {
      // First check for dotypos_employee_id match (highest priority - prevents duplicates)
      const dotyposIdMatch = existingEmployees?.find(
        (existing) =>
          existing.dotypos_employee_id === dotyposEmp.id
      );

      if (dotyposIdMatch) {
        conflicts.push({
          dotyposEmployee: dotyposEmp,
          existingEmployee: dotyposIdMatch,
          suggestedAction: 'update', // Default to update since it's the same Dotypos employee
          conflictReason: 'dotypos_id_match' as any,
        });
        continue; // Skip email check if dotypos ID matches
      }

      // Check for email match (case-insensitive)
      if (dotyposEmp.email) {
        const emailMatch = existingEmployees?.find(
          (existing) =>
            existing.email.toLowerCase() === dotyposEmp.email!.toLowerCase()
        );

        if (emailMatch) {
          conflicts.push({
            dotyposEmployee: dotyposEmp,
            existingEmployee: emailMatch,
            suggestedAction: 'skip', // Default to skip, user can change
            conflictReason: 'email_match',
          });
        }
      }
    }

    return conflicts;
  }

  /**
   * Detect email issues for employees being imported
   * Issues include: missing emails, duplicates in TagInn, duplicates within batch
   */
  static async detectEmailIssues(
    businessId: string,
    dotyposEmployees: DotyposEmployee[],
    conflicts: ImportConflict[]
  ): Promise<EmailIssue[]> {
    const issues: EmailIssue[] = [];

    // Get IDs of employees that will be skipped or linked due to conflicts
    const conflictedIds = new Set(conflicts.map(c => c.dotyposEmployee.id));

    // Fetch existing employees for duplicate checking
    const { data: existingEmployees, error } = await supabase
      .from('employees')
      .select('id, name, email')
      .eq('business_id', businessId);

    if (error) {
      throw new Error(`Failed to fetch existing employees: ${error.message}`);
    }

    const existingEmailMap = new Map(
      existingEmployees?.map(e => [e.email.toLowerCase(), e]) || []
    );

    // Track emails within the import batch for duplicate detection
    const batchEmailMap = new Map<string, DotyposEmployee[]>();

    // First pass: group employees by email within the batch
    for (const emp of dotyposEmployees) {
      // Skip employees that are already in conflicts (they'll be handled there)
      if (conflictedIds.has(emp.id)) continue;

      if (emp.email) {
        const emailLower = emp.email.toLowerCase();
        const existing = batchEmailMap.get(emailLower) || [];
        existing.push(emp);
        batchEmailMap.set(emailLower, existing);
      }
    }

    // Second pass: detect issues
    const processedBatchDuplicates = new Set<string>();

    for (const emp of dotyposEmployees) {
      // Skip employees that are already in conflicts
      if (conflictedIds.has(emp.id)) continue;

      // Check for missing email
      if (!emp.email) {
        issues.push({
          dotyposEmployee: emp,
          issueType: 'missing',
        });
        continue;
      }

      const emailLower = emp.email.toLowerCase();

      // Check for duplicate in TagInn (that wasn't caught by conflict detection)
      const existingInTaginn = existingEmailMap.get(emailLower);
      if (existingInTaginn) {
        // This shouldn't happen often since conflicts catch this, but handle edge cases
        issues.push({
          dotyposEmployee: emp,
          issueType: 'duplicate_in_taginn',
          existingEmployee: existingInTaginn,
        });
        continue;
      }

      // Check for duplicate within import batch
      const batchDuplicates = batchEmailMap.get(emailLower) || [];
      if (batchDuplicates.length > 1 && !processedBatchDuplicates.has(emailLower)) {
        // Add issue for each employee with this duplicate email
        for (const dupEmp of batchDuplicates) {
          issues.push({
            dotyposEmployee: dupEmp,
            issueType: 'duplicate_in_batch',
            duplicateEmployees: batchDuplicates.filter(e => e.id !== dupEmp.id),
          });
        }
        processedBatchDuplicates.add(emailLower);
      }
    }

    return issues;
  }

  /**
   * Calculate similarity between two strings (simple Levenshtein-based)
   */
  static calculateNameSimilarity(name1: string, name2: string): number {
    const s1 = name1.toLowerCase().trim();
    const s2 = name2.toLowerCase().trim();

    if (s1 === s2) return 100;

    // Check if one contains the other
    if (s1.includes(s2) || s2.includes(s1)) {
      const shorter = s1.length < s2.length ? s1 : s2;
      const longer = s1.length < s2.length ? s2 : s1;
      return Math.round((shorter.length / longer.length) * 90);
    }

    // Split into words and check overlap
    const words1 = s1.split(/\s+/);
    const words2 = s2.split(/\s+/);

    let matchingWords = 0;
    for (const w1 of words1) {
      for (const w2 of words2) {
        if (w1 === w2 || (w1.length > 2 && w2.length > 2 && (w1.includes(w2) || w2.includes(w1)))) {
          matchingWords++;
          break;
        }
      }
    }

    const totalWords = Math.max(words1.length, words2.length);
    return Math.round((matchingWords / totalWords) * 80);
  }

  /**
   * Detect smart matches based on name similarity
   * Only for employees that don't have email conflicts or exact email matches
   */
  static async detectSmartMatches(
    businessId: string,
    dotyposEmployees: DotyposEmployee[],
    conflicts: ImportConflict[]
  ): Promise<SmartMatch[]> {
    const matches: SmartMatch[] = [];

    // Get IDs of employees that already have conflicts (email-based)
    const conflictedIds = new Set(conflicts.map(c => c.dotyposEmployee.id));

    // Fetch existing employees
    const { data: existingEmployees, error } = await supabase
      .from('employees')
      .select('id, name, email, dotypos_employee_id')
      .eq('business_id', businessId);

    if (error || !existingEmployees) {
      return matches;
    }

    // Use all existing employees for matching (don't filter by dotypos_employee_id)
    // This allows re-linking if needed
    const availableEmployees = existingEmployees;

    // Track which TagInn employees have been suggested to avoid duplicate suggestions
    const suggestedEmployeeIds = new Set<string>();

    for (const dotyposEmp of dotyposEmployees) {
      // Skip if already has a conflict (email match or dotypos_id match)
      if (conflictedIds.has(dotyposEmp.id)) continue;

      // Find best name match from available employees
      let bestMatch: { employee: typeof availableEmployees[0]; similarity: number } | null = null;

      for (const existing of availableEmployees) {
        // Skip if this TagInn employee was already suggested to another Dotypos employee
        if (suggestedEmployeeIds.has(existing.id)) continue;

        const similarity = this.calculateNameSimilarity(dotyposEmp.name, existing.name);

        // Only consider matches with similarity >= 70%
        if (similarity >= 70) {
          if (!bestMatch || similarity > bestMatch.similarity) {
            bestMatch = { employee: existing, similarity };
          }
        }
      }

      if (bestMatch) {
        // Mark this TagInn employee as suggested
        suggestedEmployeeIds.add(bestMatch.employee.id);

        matches.push({
          dotyposEmployee: dotyposEmp,
          suggestedEmployee: {
            id: bestMatch.employee.id,
            name: bestMatch.employee.name,
            email: bestMatch.employee.email,
          },
          matchType: bestMatch.similarity === 100 ? 'exact_name' : 'similar_name',
          similarity: bestMatch.similarity,
        });
      }
    }

    return matches;
  }

  /**
   * Generate a placeholder email for an employee
   */
  static generatePlaceholderEmail(dotyposId: number): string {
    return `dotypos_${dotyposId}@placeholder.local`;
  }

  /**
   * Check if an email is a placeholder email
   */
  static isPlaceholderEmail(email: string): boolean {
    return email.endsWith('@placeholder.local');
  }

  /**
   * Filter employees based on import settings
   */
  static filterEmployees(
    employees: DotyposEmployee[],
    skipDeleted: boolean = true,
    skipDisabled: boolean = false
  ): DotyposEmployee[] {
    return employees.filter((emp) => {
      if (skipDeleted && emp.deleted) return false;
      if (skipDisabled && !emp.enabled) return false;
      return true;
    });
  }

  /**
   * Import a single employee into TagInn
   */
  static async importEmployee(
    businessId: string,
    mappedEmployee: MappedEmployee,
    action: 'create' | 'update',
    existingEmployeeId?: string
  ): Promise<ImportEmployeeResult> {
    try {
      if (action === 'create') {
        // Create new employee
        const { data, error } = await supabase
          .from('employees')
          .insert({
            business_id: businessId,
            name: mappedEmployee.name,
            email: mappedEmployee.email,
            role_id: mappedEmployee.roleId,
            contract_status: mappedEmployee.contractStatus,
            dotypos_employee_id: mappedEmployee.dotyposId, // Store Dotypos ID for attendance sync
          })
          .select('id')
          .single();

        if (error) throw error;

        return {
          dotyposId: mappedEmployee.dotyposId,
          dotyposName: mappedEmployee.name,
          success: true,
          action: 'created',
          employeeId: data.id,
        };
      } else {
        // Update existing employee
        if (!existingEmployeeId) {
          throw new Error('Employee ID required for update');
        }

        const { error } = await supabase
          .from('employees')
          .update({
            name: mappedEmployee.name,
            email: mappedEmployee.email,
            role_id: mappedEmployee.roleId,
            contract_status: mappedEmployee.contractStatus,
            dotypos_employee_id: mappedEmployee.dotyposId, // Update Dotypos ID mapping
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingEmployeeId);

        if (error) throw error;

        return {
          dotyposId: mappedEmployee.dotyposId,
          dotyposName: mappedEmployee.name,
          success: true,
          action: 'updated',
          employeeId: existingEmployeeId,
        };
      }
    } catch (error: any) {
      return {
        dotyposId: mappedEmployee.dotyposId,
        dotyposName: mappedEmployee.name,
        success: false,
        action: 'failed',
        error: error.message || 'Unknown error',
      };
    }
  }

  /**
   * Import multiple employees with conflict, smart match, and email resolution
   */
  static async importEmployees(
    businessId: string,
    dotyposEmployees: DotyposEmployee[],
    conflicts: ImportConflict[],
    conflictResolutions: Map<number, 'skip' | 'update' | 'create'>,
    smartMatches: SmartMatch[],
    smartMatchResolutions: Map<number, SmartMatchResolution>,
    emailResolutions: Map<number, EmailResolution>,
    defaultRoleId?: string | null
  ): Promise<ImportSummary> {
    const startedAt = new Date().toISOString();
    const results: ImportEmployeeResult[] = [];

    for (const dotyposEmp of dotyposEmployees) {
      // Check if this employee has an email resolution that says skip
      const emailRes = emailResolutions.get(dotyposEmp.id);
      if (emailRes?.resolutionType === 'skip') {
        results.push({
          dotyposId: dotyposEmp.id,
          dotyposName: dotyposEmp.name,
          success: true,
          action: 'skipped',
        });
        continue;
      }

      // Check if this employee has an accepted smart match
      const smartMatch = smartMatches.find(m => m.dotyposEmployee.id === dotyposEmp.id);
      const smartMatchRes = smartMatchResolutions.get(dotyposEmp.id);

      if (smartMatch && smartMatchRes === 'accept') {
        // Update the suggested employee with Dotypos data
        const mapped = this.mapEmployee(dotyposEmp, defaultRoleId);
        const result = await this.importEmployee(
          businessId,
          mapped,
          'update',
          smartMatch.suggestedEmployee.id
        );
        results.push(result);
        continue;
      }

      // Check if this employee should link to existing (from email resolution)
      // Both 'link_existing' and 'assimilate' work the same way - update existing employee
      if ((emailRes?.resolutionType === 'link_existing' || emailRes?.resolutionType === 'assimilate') && emailRes.linkToEmployeeId) {
        // Update existing employee with Dotypos data
        const mapped = this.mapEmployee(dotyposEmp, defaultRoleId);
        const result = await this.importEmployee(
          businessId,
          mapped,
          'update',
          emailRes.linkToEmployeeId
        );
        results.push(result);
        continue;
      }

      // Determine email to use based on resolution
      let emailOverride: string | undefined;
      if (emailRes) {
        if (emailRes.resolutionType === 'manual' && emailRes.manualEmail) {
          emailOverride = emailRes.manualEmail;
        } else if (emailRes.resolutionType === 'generate_placeholder') {
          emailOverride = this.generatePlaceholderEmail(dotyposEmp.id);
        }
      }

      // Check if this employee has a conflict
      const conflict = conflicts.find(
        (c) => c.dotyposEmployee.id === dotyposEmp.id
      );

      if (conflict) {
        const resolution = conflictResolutions.get(dotyposEmp.id) || 'skip';

        if (resolution === 'skip') {
          results.push({
            dotyposId: dotyposEmp.id,
            dotyposName: dotyposEmp.name,
            success: true,
            action: 'skipped',
          });
          continue;
        }

        // Map and import with resolution
        const mapped = this.mapEmployee(dotyposEmp, defaultRoleId, emailOverride);
        const result = await this.importEmployee(
          businessId,
          mapped,
          resolution === 'update' ? 'update' : 'create',
          resolution === 'update' ? conflict.existingEmployee.id : undefined
        );
        results.push(result);
      } else {
        // No conflict, create new employee
        const mapped = this.mapEmployee(dotyposEmp, defaultRoleId, emailOverride);
        const result = await this.importEmployee(businessId, mapped, 'create');
        results.push(result);
      }
    }

    const completedAt = new Date().toISOString();

    // Calculate summary
    const summary: ImportSummary = {
      total: results.length,
      created: results.filter((r) => r.action === 'created').length,
      updated: results.filter((r) => r.action === 'updated').length,
      skipped: results.filter((r) => r.action === 'skipped').length,
      failed: results.filter((r) => r.action === 'failed').length,
      results,
      startedAt,
      completedAt,
    };

    return summary;
  }

  /**
   * Save Dotypos configuration to database (encrypted)
   */
  static async saveConfig(config: Omit<DotyposConfig, 'created_at' | 'updated_at'>): Promise<void> {
    // Note: In production, client_secret should be encrypted before storage
    // For now, we'll call an Edge Function to handle encryption
    const { error } = await supabase.functions.invoke('save-dotypos-config', {
      body: config,
    });

    if (error) {
      throw new Error(`Failed to save Dotypos config: ${error.message}`);
    }
  }

  /**
   * Load Dotypos configuration from database
   */
  static async loadConfig(businessId: string): Promise<DotyposConfig | null> {
    // First try to load from businesses table (where config is actually stored)
    const { data: business, error: businessError } = await supabase
      .from('businesses')
      .select('dotypos_enabled, dotypos_refresh_token, dotypos_cloud_id')
      .eq('id', businessId)
      .single();

    if (businessError) {
      console.error('Failed to load business Dotypos config:', businessError);
      return null;
    }

    if (business?.dotypos_enabled && business?.dotypos_refresh_token && business?.dotypos_cloud_id) {
      // Config exists in businesses table - convert to DotyposConfig format
      // Note: We need to get access token from refresh token for API calls
      return {
        business_id: businessId,
        client_id: '', // Not stored, will use refresh token approach
        client_secret: '', // Not stored, will use refresh token approach
        cloud_id: business.dotypos_cloud_id,
        enabled: business.dotypos_enabled,
        refresh_token: business.dotypos_refresh_token,
        import_settings: {
          skipDeletedEmployees: true,
          importDisabledEmployees: false,
        },
        created_at: '',
        updated_at: '',
      };
    }

    // Fallback: try Edge Function if not in businesses table
    try {
      const { data, error } = await supabase.functions.invoke('get-dotypos-config', {
        body: { business_id: businessId },
      });

      if (error) {
        console.error('Failed to load Dotypos config from Edge Function:', error);
        return null;
      }

      return data as DotyposConfig | null;
    } catch (e) {
      console.error('Edge Function call failed:', e);
      return null;
    }
  }

  /**
   * Delete Dotypos configuration
   */
  static async deleteConfig(businessId: string): Promise<void> {
    const { error } = await supabase.functions.invoke('delete-dotypos-config', {
      body: { business_id: businessId },
    });

    if (error) {
      throw new Error(`Failed to delete Dotypos config: ${error.message}`);
    }
  }

  // ========================================
  // EXPORT (Push to Dotypos) Methods
  // ========================================

  /**
   * Push a single employee to Dotypos
   */
  static async pushEmployee(
    businessId: string,
    employeeId: string,
    forceCreate: boolean = false
  ): Promise<PushEmployeeResult> {
    const apiBase = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';

    const response = await fetch(`${apiBase}/dotypos/push-employee`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        business_id: businessId,
        employee_id: employeeId,
        force_create: forceCreate,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        taginnEmployeeId: employeeId,
        taginnEmployeeName: '',
        dotyposId: null,
        success: false,
        action: 'failed',
        error: data.error || 'Failed to push employee to Dotypos',
      };
    }

    return {
      taginnEmployeeId: data.taginnEmployeeId,
      taginnEmployeeName: data.taginnEmployeeName,
      dotyposId: data.dotyposId,
      success: true,
      action: data.action,
    };
  }

  /**
   * Push multiple employees to Dotypos
   */
  static async pushEmployeesBulk(
    businessId: string,
    employeeIds: string[],
    mode: ExportMode = 'sync_all'
  ): Promise<PushEmployeesSummary> {
    const apiBase = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';

    const response = await fetch(`${apiBase}/dotypos/push-employees-bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        business_id: businessId,
        employee_ids: employeeIds,
        mode: mode,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to push employees to Dotypos');
    }

    return {
      total: data.total,
      created: data.created,
      updated: data.updated,
      linked: data.linked,
      skipped: data.skipped,
      failed: data.failed,
      results: data.results,
    };
  }

  /**
   * Get sync status for employees
   */
  static async getEmployeeSyncStatus(
    businessId: string,
    employeeIds?: string[]
  ): Promise<EmployeeSyncInfo[]> {
    let query = supabase
      .from('employees')
      .select('id, name, dotypos_employee_id, dotypos_synced_at, dotypos_sync_error')
      .eq('business_id', businessId);

    if (employeeIds && employeeIds.length > 0) {
      query = query.in('id', employeeIds);
    }

    const { data: employees, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch employee sync status: ${error.message}`);
    }

    return (employees || []).map((emp) => {
      let syncStatus: DotyposSyncStatus = 'not_synced';

      if (emp.dotypos_employee_id) {
        if (emp.dotypos_sync_error) {
          syncStatus = 'error';
        } else {
          syncStatus = 'synced';
        }
      }

      return {
        employeeId: emp.id,
        employeeName: emp.name,
        dotyposId: emp.dotypos_employee_id,
        syncStatus,
        syncedAt: emp.dotypos_synced_at,
        syncError: emp.dotypos_sync_error,
      };
    });
  }

  /**
   * Get employees that can be pushed to Dotypos
   * (excludes placeholder emails)
   */
  static async getExportableEmployees(businessId: string): Promise<Employee[]> {
    const { data: employees, error } = await supabase
      .from('employees')
      .select('*')
      .eq('business_id', businessId)
      .not('email', 'like', '%@placeholder.local');

    if (error) {
      throw new Error(`Failed to fetch exportable employees: ${error.message}`);
    }

    return employees || [];
  }

  /**
   * Sync access level to all Dotypos-linked employees with a specific role
   * Called when a role's dotypos_access_level is changed
   */
  static async syncRoleAccessLevel(
    businessId: string,
    roleId: string,
    accessLevel: number
  ): Promise<{ updated: number; failed: number; results: Array<{ employeeId: string; employeeName: string; success: boolean; error?: string }> }> {
    const apiBase = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';

    const response = await fetch(`${apiBase}/dotypos/sync-role-access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        business_id: businessId,
        role_id: roleId,
        access_level: accessLevel,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to sync role access level');
    }

    return {
      updated: data.updated,
      failed: data.failed,
      results: data.results || [],
    };
  }
}
